export class AudioMonitor {
  private audioContext: AudioContext;
  private gainNode: GainNode;
  private sources: Map<string, MediaStreamAudioSourceNode> = new Map();
  private destination: MediaStreamAudioDestinationNode;
  private audioElement: HTMLAudioElement;
  private isMuted: boolean = false;

  constructor() {
    this.audioContext = new AudioContext({ sampleRate: 48000 });
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = 0.3; // Default 30% to prevent feedback
    
    this.destination = this.audioContext.createMediaStreamDestination();
    this.gainNode.connect(this.destination);
    
    // Create audio element for playback
    this.audioElement = new Audio();
    this.audioElement.srcObject = this.destination.stream;
    this.audioElement.autoplay = true;
    
    console.log('[AudioMonitor] Initialized with 30% volume');
  }

  addStream(id: string, stream: MediaStream): void {
    console.log('[AudioMonitor] Adding stream:', id);
    
    // Remove existing source if any
    this.removeStream(id);

    try {
      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.gainNode);
      this.sources.set(id, source);
      
      console.log('[AudioMonitor] Stream added successfully:', id);
    } catch (error) {
      console.error('[AudioMonitor] Error adding stream:', error);
    }
  }

  removeStream(id: string): void {
    const source = this.sources.get(id);
    if (source) {
      console.log('[AudioMonitor] Removing stream:', id);
      source.disconnect();
      this.sources.delete(id);
    }
  }

  setVolume(volume: number): void {
    // Clamp between 0 and 1, with max at 0.5 to prevent feedback
    const safeVolume = Math.max(0, Math.min(0.5, volume));
    this.gainNode.gain.value = safeVolume;
    console.log('[AudioMonitor] Volume set to:', safeVolume);
  }

  mute(): void {
    this.isMuted = true;
    this.gainNode.gain.value = 0;
    console.log('[AudioMonitor] Muted');
  }

  unmute(): void {
    this.isMuted = false;
    this.gainNode.gain.value = 0.3; // Restore to default safe volume
    console.log('[AudioMonitor] Unmuted');
  }

  dispose(): void {
    console.log('[AudioMonitor] Disposing...');
    
    // Disconnect all sources
    this.sources.forEach(source => source.disconnect());
    this.sources.clear();
    
    // Stop audio element
    this.audioElement.pause();
    this.audioElement.srcObject = null;
    
    // Disconnect gain node
    this.gainNode.disconnect();
    
    // Close audio context
    this.audioContext.close();
  }
}
